import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getJobByPO, getJobs, getJob, updateJob, deleteJob } from '../utils/api';
import JobForm from '../components/JobForm';
import toast from 'react-hot-toast';

export default function UpdateRecord() {
  const [searchParams] = useSearchParams();
  const [po, setPo] = useState(searchParams.get('po') || '');
  const [searchInput, setSearchInput] = useState(searchParams.get('po') || '');
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [results, setResults] = useState(null); // null = no search yet, [] = none found
  const navigate = useNavigate();

  useEffect(() => {
    if (po) loadJob(po);
  }, []);

  async function loadJob(poNum) {
    setLoading(true);
    setJob(null);
    try {
      const data = await getJobByPO(poNum);
      setJob(data);
    } catch {
      toast.error(`PO# ${poNum} not found`);
    } finally {
      setLoading(false);
    }
  }

  async function openJob(id, poNum) {
    setLoading(true);
    setResults(null);
    setJob(null);
    try {
      const data = await getJob(id);
      setJob(data);
      setPo(data.po_number || poNum || '');
      navigate(`/update?po=${encodeURIComponent(data.po_number || poNum || '')}`, { replace: true });
    } catch {
      toast.error('Failed to load record');
    } finally {
      setLoading(false);
    }
  }

  async function handleSearch(e) {
    e.preventDefault();
    const q = searchInput.trim();
    if (!q) return;
    setLoading(true);
    setJob(null);
    setResults(null);
    try {
      // 1) exact PO match loads directly (keeps ?po= links behaviour)
      try {
        const data = await getJobByPO(q);
        setJob(data);
        setPo(q);
        navigate(`/update?po=${encodeURIComponent(q)}`, { replace: true });
        return;
      } catch { /* not a PO — fall through to broad search */ }

      // 2) broad search: PO, name, address, city, phone, email
      const { jobs } = await getJobs({ search: q, limit: 25 });
      if (!jobs || jobs.length === 0) {
        setPo(q);
        setResults([]);
        toast.error(`No records match "${q}"`);
      } else if (jobs.length === 1) {
        await openJob(jobs[0].id, jobs[0].po_number);
      } else {
        setPo('');
        setResults(jobs);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Search failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(data) {
    setSaving(true);
    try {
      await updateJob(job.id, data);
      toast.success(`Record updated: ${data.po_number}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete record ${job.po_number} — ${job.customer_name}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await deleteJob(job.id);
      toast.success(`Record deleted: ${job.po_number}`);
      setJob(null);
      setPo('');
      setSearchInput('');
      setResults(null);
      navigate('/update', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <div className="page-header">
        <h2>Update Record</h2>
      </div>
      <div className="page-body">
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-body">
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
              <div className="form-group" style={{ flex: 1, maxWidth: 360 }}>
                <label className="form-label">Search by PO, name, address, or phone</label>
                <input
                  className="form-input"
                  placeholder="e.g. 26-1261-1, Gladys Hill, Buena Park, (626) 522…"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  autoFocus
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Searching…' : 'Load Record'}
              </button>
            </form>
          </div>
        </div>

        {loading && <div className="loader"><div className="spinner" /></div>}

        {job && (
          <div className="card">
            <div className="card-header">
              <h3>{job.customer_name} — {job.po_number}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className="badge" style={{ background: 'var(--green-100)', color: 'var(--green-700)' }}>
                  {job.sales_status}
                </span>
                <button
                  className="btn btn-sm"
                  style={{ background: '#c62828', color: '#fff', border: 'none' }}
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting…' : 'Delete Record'}
                </button>
              </div>
            </div>
            <div className="card-body">
              <JobForm initial={job} onSubmit={handleSubmit} submitLabel="Update Record" loading={saving} jobId={job.id} />
            </div>
          </div>
        )}

        {results && results.length > 1 && !job && !loading && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header"><h3>{results.length} matching records — pick one</h3></div>
            <div className="card-body" style={{ padding: 0 }}>
              <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
                <table>
                  <thead>
                    <tr><th>PO#</th><th>Customer</th><th>Address</th><th>Phone</th><th>Status</th><th></th></tr>
                  </thead>
                  <tbody>
                    {results.map(r => (
                      <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => openJob(r.id, r.po_number)}>
                        <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{r.po_number}</td>
                        <td>{r.customer_name}{r.customer_name_2 ? ` / ${r.customer_name_2}` : ''}</td>
                        <td>{[r.address, r.city].filter(Boolean).join(', ')}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{r.phone || r.phone_2 || ''}</td>
                        <td>{r.sales_status || ''}</td>
                        <td><button type="button" className="btn btn-sm btn-primary" onClick={ev => { ev.stopPropagation(); openJob(r.id, r.po_number); }}>Open</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {!job && !loading && po && (!results || results.length === 0) && (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <p>No records match "{po}"</p>
          </div>
        )}
      </div>
    </>
  );
}
